/**
 * Product Related Events (Storefront API)
 * Fetches event metaobjects and filters them to the current product.
 */

(() => {
  const wrapper = document.querySelector("[data-product-related-events]");
  if (!wrapper) return;

  const list = wrapper.querySelector("[data-product-related-events-list]");
  const loader = wrapper.querySelector("[data-product-related-events-loading]");
  const errorMessage = wrapper.querySelector(
    "[data-product-related-events-error]",
  );

  const token = wrapper.dataset.storefrontToken;
  const apiVersion = wrapper.dataset.storefrontApiVersion || "2024-10";
  const productHandle = String(wrapper.dataset.productHandle || "").trim();

  const productAuthorIds = new Set(
    String(wrapper.dataset.productAuthorIds || "")
      .split("|")
      .map((v) => String(v).trim())
      .filter(Boolean),
  );

  if (!token || !productHandle || !list) return;

  const endpoint = `${window.location.origin}/api/${apiVersion}/graphql.json`;

  const CACHE_VERSION = "1";
  const CACHE_TTL_MS = 30 * 60 * 1000;
  const cacheKey = `product-related-events:v${CACHE_VERSION}:${productHandle}`;

  const escapeHtml = (value) => {
    return String(value ?? "")
      .replaceAll("&", "&amp;")
      .replaceAll("<", "&lt;")
      .replaceAll(">", "&gt;")
      .replaceAll('"', "&quot;")
      .replaceAll("'", "&#39;");
  };

  const parseIsoDateKey = (value) => {
    const raw = String(value ?? "").trim();
    if (/^\d{4}-\d{2}-\d{2}$/.test(raw)) return raw;
    return "";
  };

  const formatDateForRow = (isoDate) => {
    if (!isoDate) return "";

    const [y, m, d] = isoDate.split("-").map((n) => Number(n));
    if (!y || !m || !d) return "";

    const date = new Date(Date.UTC(y, m - 1, d));
    const locale = document.documentElement.lang || navigator.language || "en";

    try {
      const day = String(d).padStart(2, "0");
      const month = new Intl.DateTimeFormat(locale, {
        month: "short",
        timeZone: "UTC",
      }).format(date);
      const year = String(y);
      return `${day} ${month} ${year}`;
    } catch {
      return `${String(d).padStart(2, "0")} ${String(m).padStart(2, "0")} ${y}`;
    }
  };

  const setLoadingState = (isLoading) => {
    if (isLoading) wrapper.hidden = false;
    wrapper.setAttribute("aria-busy", isLoading ? "true" : "false");
    if (loader) loader.hidden = !isLoading;
    if (errorMessage) errorMessage.hidden = true;
  };

  const showErrorState = () => {
    wrapper.hidden = false;
    wrapper.setAttribute("aria-busy", "false");
    if (loader) loader.hidden = true;
    if (errorMessage) errorMessage.hidden = false;
  };

  const storefrontRequest = async (query, variables) => {
    const response = await fetch(endpoint, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "X-Shopify-Storefront-Access-Token": token,
      },
      body: JSON.stringify({ query, variables }),
    });

    if (!response.ok) {
      throw new Error(`Storefront API request failed (${response.status})`);
    }

    const payload = await response.json();

    if (payload.errors?.length) {
      const message = payload.errors.map((e) => e.message).join("; ");
      throw new Error(message);
    }

    return payload.data;
  };

  const readCache = () => {
    try {
      const raw = sessionStorage.getItem(cacheKey);
      if (!raw) return null;
      const parsed = JSON.parse(raw);
      const timestamp = Number(parsed?.timestamp || 0);
      if (!timestamp || Date.now() - timestamp > CACHE_TTL_MS) return null;

      const events = Array.isArray(parsed?.events) ? parsed.events : [];
      return events
        .map((e) => {
          const handle = String(e?.handle || "").trim();
          if (!handle) return null;
          return {
            handle,
            title: String(e?.title || ""),
            isoDate: parseIsoDateKey(e?.isoDate),
            location: String(e?.location || ""),
          };
        })
        .filter(Boolean);
    } catch {
      return null;
    }
  };

  const writeCache = (events) => {
    try {
      sessionStorage.setItem(
        cacheKey,
        JSON.stringify({
          timestamp: Date.now(),
          events: events.map((e) => ({
            handle: e.handle,
            title: e.title,
            isoDate: e.isoDate,
            location: e.location,
          })),
        }),
      );
    } catch {
      // Ignore storage errors (private mode, quota, etc.)
    }
  };

  const renderEvents = (events) => {
    if (!events.length) {
      wrapper.hidden = true;
      return;
    }

    const html = events
      .map((event) => {
        const safeHandle = escapeHtml(event.handle);
        const safeTitle = escapeHtml(event.title);
        const href = `/pages/event/${safeHandle}`;

        const dateHtml = event.isoDate
          ? `
            <time class="product-read-more__date" datetime="${escapeHtml(event.isoDate)}">
              ${escapeHtml(formatDateForRow(event.isoDate))}
            </time>
          `
          : "";

        const locationText = String(event.location || "").trim();
        const locationHtml = locationText
          ? `
              <p class="product-read-more__location">${escapeHtml(locationText)}</p>
            `
          : "";

        return `
          <article class="product-read-more__item">
            <a href="${escapeHtml(href)}" class="product-read-more__link">
              ${dateHtml}
              <div class="product-read-more__content">
                <h4 class="product-read-more__event-title">${safeTitle}</h4>
                ${locationHtml}
              </div>
            </a>
          </article>
        `;
      })
      .join("");

    list.innerHTML = html;
    wrapper.hidden = false;
  };

  const fetchRelatedEvents = async () => {
    const query = `
      query RelatedEvents($first: Int!, $after: String) {
        metaobjects(type: "event", first: $first, after: $after) {
          pageInfo { hasNextPage endCursor }
          edges {
            node {
              id
              handle
              title: field(key: "title") { value }
              date: field(key: "date") { value }
              location: field(key: "location") { value }
              books: field(key: "books") {
                references(first: 50) {
                  nodes {
                    ... on Product {
                      id
                      handle
                    }
                  }
                }
              }
              authors: field(key: "authors") {
                references(first: 50) {
                  nodes {
                    ... on Metaobject {
                      id
                    }
                  }
                }
              }
            }
          }
        }
      }
    `;

    const related = [];
    let after = null;

    for (;;) {
      const data = await storefrontRequest(query, { first: 100, after });
      const connection = data?.metaobjects;
      const edges = connection?.edges ?? [];

      for (const edge of edges) {
        const node = edge?.node;
        const handle = String(node?.handle || "").trim();
        if (!handle) continue;

        const bookHandles = new Set(
          (node?.books?.references?.nodes ?? [])
            .map((p) => String(p?.handle || "").trim())
            .filter(Boolean),
        );

        let isRelated = bookHandles.has(productHandle);

        if (!isRelated && productAuthorIds.size) {
          const eventAuthorIds = (node?.authors?.references?.nodes ?? [])
            .map((a) => String(a?.id || "").trim())
            .filter(Boolean);

          for (const authorId of eventAuthorIds) {
            if (productAuthorIds.has(authorId)) {
              isRelated = true;
              break;
            }
          }
        }

        if (!isRelated) continue;

        related.push({
          handle,
          title: node?.title?.value ?? "",
          isoDate: parseIsoDateKey(node?.date?.value),
          location: node?.location?.value ?? "",
        });
      }

      if (!connection?.pageInfo?.hasNextPage) break;
      after = connection.pageInfo.endCursor;
      if (!after) break;
    }

    related.sort((a, b) => {
      if (a.isoDate && b.isoDate) {
        if (a.isoDate > b.isoDate) return -1;
        if (a.isoDate < b.isoDate) return 1;
      } else if (a.isoDate && !b.isoDate) {
        return -1;
      } else if (!a.isoDate && b.isoDate) {
        return 1;
      }

      const aTitle = String(a.title ?? "").toLowerCase();
      const bTitle = String(b.title ?? "").toLowerCase();
      if (aTitle < bTitle) return -1;
      if (aTitle > bTitle) return 1;
      return 0;
    });

    return related;
  };

  const main = async () => {
    setLoadingState(true);

    const cached = readCache();
    if (cached) {
      renderEvents(cached);
      setLoadingState(false);
      return;
    }

    try {
      const related = await fetchRelatedEvents();
      setLoadingState(false);

      if (!related.length) {
        wrapper.hidden = true;
        return;
      }

      renderEvents(related);
      writeCache(related);
    } catch (error) {
      console.error(error);
      showErrorState();
    }
  };

  main();
})();
